import { useEffect, useRef, useState } from 'react'
import { ListChecks, Plus, X, Sparkles, ChevronDown, ChevronRight } from 'lucide-react'
import type { TaskStep } from '../../services/taskService'

/**
 * A short, editable checklist on an item (owner 2026-09-05 for tasks: "should
 * just be a short list"; 2026-09-06 for habits: "like Steps for a task — a
 * list that allows edits and additions"). Generic over what saves it: the
 * caller passes the steps and an `onSave`; tasks and habits wrap it.
 */
export interface StepsListProps {
  steps: TaskStep[]
  onSave: (next: TaskStep[]) => Promise<void>
  /** Editable at all (an open task, a live habit). */
  open: boolean
  /** Whether ticking a step means anything here. A habit's steps are ticked
   *  each time it comes round, not on the habit itself. */
  tickable?: boolean
  /** Ticks that belong to a day rather than the list (a habit's today): the
   *  ids ticked, and the handler that records a tick. `done` on the steps
   *  themselves is ignored while these are given. */
  tickedIds?: Set<string>
  onTick?: (id: string, done: boolean) => Promise<void>
  /** A quiet line under the heading, e.g. "for today". */
  scope?: string
  /** Extra rows that are not embedded steps (a task's older action items). */
  extra?: React.ReactNode
  extraCount?: number
  extraDone?: number
  onBreakdown?: () => Promise<void> | void
  breakdownBusy?: boolean
  hasAi?: boolean
  heading?: boolean
  title?: string
  /** Remembers expanded / collapsed per item on this device. */
  storeKey: string
  openRequest?: number
  testId?: string
  emptyWords?: string
}

const HEADING = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary'

export function StepsList({
  steps, onSave, open, tickable = true, extra, extraCount = 0, extraDone = 0, onBreakdown, breakdownBusy = false, hasAi = false,
  heading = true, title = 'Steps', storeKey, openRequest = 0, testId = 'steps', emptyWords = 'No steps were written down.',
  tickedIds, onTick, scope,
}: StepsListProps) {
  const dayTicks = Boolean(tickedIds && onTick)
  const isDone = (s: TaskStep) => (dayTicks ? Boolean(tickedIds!.has(s.id)) : s.done)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const doneCount = steps.filter(isDone).length + extraDone
  const total = steps.length + extraCount
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { const v = localStorage.getItem(storeKey); if (v === '1') return true; if (v === '0') return false } catch { /* no storage */ }
    return total > 0
  })
  const rootRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!openRequest) return
    setExpanded(true)
    try { localStorage.setItem(storeKey, '1') } catch { /* no storage */ }
    rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [openRequest, storeKey])
  const toggleExpanded = () => setExpanded((e) => { try { localStorage.setItem(storeKey, e ? '0' : '1') } catch { /* no storage */ } return !e })

  const save = async (next: TaskStep[]) => {
    setBusy(true); setErr(null)
    try { await onSave(next) }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not save the steps') }
    finally { setBusy(false) }
  }
  const tick = async (id: string) => {
    if (dayTicks) {
      setBusy(true); setErr(null)
      try { await onTick!(id, !tickedIds!.has(id)) }
      catch (e) { setErr(e instanceof Error ? e.message : 'Could not record that') }
      finally { setBusy(false) }
      return
    }
    await save(steps.map((s) => (s.id === id ? { ...s, done: !s.done, done_at: !s.done ? new Date().toISOString() : null } : s)))
  }
  const remove = (id: string) => save(steps.filter((s) => s.id !== id))
  const add = async () => {
    const text = draft.trim()
    if (!text) return
    setDraft('')
    await save([...steps, { id: `u${Date.now()}`, text, done: false, source: 'user' }])
  }
  const startEdit = (s: TaskStep) => { setEditing(s.id); setEditText(s.text) }
  const commitEdit = async () => {
    const id = editing
    const text = editText.trim()
    setEditing(null)
    if (!id || !text) return
    if (steps.find((s) => s.id === id)?.text === text) return
    await save(steps.map((s) => (s.id === id ? { ...s, text } : s)))
  }

  return (
    <section ref={rootRef} data-testid={testId} aria-busy={busy} className="scroll-mt-4">
      {heading && (
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={toggleExpanded} aria-expanded={expanded} aria-controls={`${testId}-body`} className={`${HEADING} rounded hover:text-primary`} data-testid={`${testId}-toggle`}>
            {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-muted" />}
            <ListChecks className="h-3.5 w-3.5 text-brand" /> {title}
            <span className="normal-case tracking-normal font-medium text-muted">{total > 0 ? (tickable ? `· ${doneCount} of ${total} done${scope ? ` ${scope}` : ''}` : `· ${total}`) : '· none yet'}</span>
          </button>
          {open && onBreakdown && !hasAi && (
            <button type="button" onClick={() => void onBreakdown()} disabled={breakdownBusy} className="inline-flex items-center gap-1 text-app-meta font-medium text-brand hover:underline disabled:opacity-60" data-testid={`${testId}-breakdown`}>
              <Sparkles className="h-3.5 w-3.5" /> {breakdownBusy ? 'Working…' : 'Break into steps'}
            </button>
          )}
        </div>
      )}

      <div id={`${testId}-body`} hidden={!expanded}>
        {total > 0 && (
          <ul className="mt-2 space-y-1" data-testid={`${testId}-list`}>
            {steps.map((s, i) => (
              <li key={s.id} className="group flex items-start gap-2 rounded-md px-1 py-1 hover:bg-surface-alt">
                {tickable ? (
                  <input type="checkbox" checked={isDone(s)} disabled={!open || busy} onChange={() => void tick(s.id)} aria-label={`${isDone(s) ? 'Untick' : 'Tick'} step: ${s.text}`} className="mt-1 h-4 w-4 accent-brand" />
                ) : (
                  <span className="mt-0.5 w-5 shrink-0 text-app-micro text-muted" aria-hidden="true">{i + 1}.</span>
                )}
                <div className="min-w-0 flex-1">
                  {editing === s.id ? (
                    <input
                      type="text" value={editText} autoFocus onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void commitEdit() } else if (e.key === 'Escape') setEditing(null) }}
                      onBlur={() => void commitEdit()} aria-label="Edit step"
                      className="w-full rounded-md border border-soft bg-surface px-1.5 py-0.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand/30"
                      data-testid={`${testId}-edit-input`}
                    />
                  ) : (
                    <button type="button" onClick={() => open && startEdit(s)} disabled={!open} className={`w-full rounded text-left text-sm ${isDone(s) ? 'text-muted line-through decoration-soft' : 'text-primary'} ${open ? 'hover:text-brand' : ''}`} title={open ? 'Edit this step' : undefined} data-testid={`${testId}-step`}>
                      {s.text}
                    </button>
                  )}
                  {(s.detail || s.minutes) && <div className="text-app-micro text-muted">{[s.detail, s.minutes ? `${s.minutes} min` : null].filter(Boolean).join(' · ')}</div>}
                </div>
                {open && (
                  <button type="button" onClick={() => void remove(s.id)} disabled={busy} aria-label={`Remove step: ${s.text}`} className="rounded p-1 text-muted opacity-0 hover:text-bad focus:opacity-100 group-hover:opacity-100">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
            {extra}
          </ul>
        )}
        {open && (
          <div className="mt-2 flex items-center gap-2">
            <Plus className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
            <input
              type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
              placeholder={total === 0 ? 'First step, one line' : 'Add a step'} aria-label="Add a step" disabled={busy}
              className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-1 text-sm text-primary placeholder:text-muted focus:border-soft focus:bg-surface focus:outline-none"
              data-testid={`${testId}-input`}
            />
            {draft.trim() && <button type="button" onClick={() => void add()} disabled={busy} className="text-app-meta font-medium text-brand hover:underline">Add</button>}
          </div>
        )}
        {!open && total === 0 && <p className="mt-1 text-sm text-muted">{emptyWords}</p>}
        {err && <p className="mt-1 text-xs text-bad" role="alert">{err}</p>}
      </div>
    </section>
  )
}

export default StepsList
